/**
 * @file AI 决策模块入口 —— `design/gdd/06-ai-strategy.md` §1.1 / §6.4 / §7。
 *
 * - `buildSeenCounts`：已见张数表（§1.1，含暗杠不计入的红线）；
 * - `decide`：决策主循环（§7），按阶段编排：定缺 → 摸牌后出牌 → 碰/杠/胡响应；
 * - `computeThinkDelayMs`：拟人化决策延迟（§6.4）。
 *
 * 纯函数、平台无关。随机性全部走注入的确定性 PRNG（`prng.ts`），
 * 相同 `(seed, difficulty, obs)` ⇒ 相同决策序列（`06` §0 目标 3 / V-7）。
 */

import type { RngState, Suit } from '../types';
import { keyOfKind, kindFromKindIndex } from '../tiles';
import { nextRandom } from '../prng';
import { canWinCounts } from '../win';
import type { AIDifficulty } from '../config';
import type {
  AIAction,
  AIObservation,
  AIDecision,
  SeenScope,
} from './types';
import type { DifficultyPreset } from './params';
import { DIFFICULTY_PRESETS } from './params';
import { copyCounts } from './shanten';
import { pickQue } from './que';
import {
  discardCost,
  getLegalDiscards,
  makeValuationContext,
  pickWithNoise,
} from './discard';
import {
  canMingGang,
  canPeng,
  gangValue,
  listSelfGangs,
  shouldGang,
  shouldHu,
  shouldPeng,
} from './claims';

export * from './types';
export * from './params';
export * from './shanten';
export * from './ukeire';
export * from './que';
export * from './discard';
export * from './claims';

/* ─────────────────────────── §1.1 已见张数表 ─────────────────────────── */

/**
 * 「已见张数」表 —— 一切估值的基础（§1.1）。
 *
 * ```ts
 * liveCount(k) = 4 - seen[k]   // 该牌种「理论上还可能摸到」的张数
 * ```
 *
 * ★ 暗杠的处理是易错点：对手暗杠占用了 4 张某牌，但 AI 不知道是哪种。
 * 正确做法是**不计入 `seen`**（保守估计，认为那些牌还在）—— 若错误地把暗杠
 * 计入某个具体牌种，就是作弊（§1.1 红线）。
 *
 * 数牌范围按难度（§6.1）：`HAND_ONLY`（简单）/ `HAND_OWN_DISCARDS`（普通）/
 * `FULL`（困难，默认）。缺省为 `FULL`，与文档 §1.1 的签名一致。
 */
export function buildSeenCounts(obs: AIObservation, scope: SeenScope = 'FULL'): Uint8Array {
  const seen = new Uint8Array(27);

  // 自己手牌
  for (let k = 0; k < 27; k++) seen[k] = obs.hand[k] ?? 0;

  // 自己副露（自己视角暗杠牌面已知，可计入）
  for (const m of obs.melds) {
    if (m.kind === null) continue;
    const k = keyOfKind(m.kind);
    seen[k] = (seen[k] ?? 0) + (m.type === 'peng' ? 3 : 4);
  }

  // 自己牌河（普通/困难档）
  if (scope === 'HAND_OWN_DISCARDS' || scope === 'FULL') {
    for (const t of obs.ownDiscards ?? []) seen[t] = (seen[t] ?? 0) + 1;
  }

  // 全场信息（困难档）
  if (scope === 'FULL') {
    for (const o of obs.opponents) {
      for (const t of o.discards) seen[t] = (seen[t] ?? 0) + 1;
      for (const m of o.melds) {
        // ★ 暗杠牌面未知，不可计入（MeldType 里暗杠是 'gang_an'）
        if (m.type === 'gang_an') continue;
        if (m.kind === null) continue; // 双保险：投影层未给牌面同样跳过
        const k = keyOfKind(m.kind);
        seen[k] = (seen[k] ?? 0) + (m.type === 'peng' ? 3 : 4);
      }
    }
  }

  return seen;
}

/* ─────────────────────────── §6.4 拟人化决策延迟 ─────────────────────────── */

/**
 * 拟人化决策延迟（§6.4）：
 *
 * ```
 * delay = baseDelay[difficulty] + jitter
 *       + (decisionComplexity > threshold ? 400ms : 0)   // 难决策"想久一点"
 *       + (isHuOrGang ? 250ms : 0)                       // 重大操作"犹豫一下"
 * ```
 *
 * 由宿主负责实际等待，core 不睡眠。**必须实现** —— 零延迟的 AI 会让玩家强烈出戏。
 *
 * @param cfg             难度预设（`delayRange` 即基础延迟范围）
 * @param rng             用于抖动；返回推进后的状态
 * @param decisionComplexity 候选决策数（≥ 4 视为「难决策」加 400ms）
 * @param isHuOrGang      胡/杠等重大操作加 250ms
 */
export function computeThinkDelayMs(
  cfg: DifficultyPreset,
  rng: RngState,
  decisionComplexity: number,
  isHuOrGang: boolean,
): { readonly delayMs: number; readonly rng: RngState } {
  const [min, max] = cfg.delayRange;
  const r = nextRandom(rng);
  const span = max - min;
  let delayMs = min + Math.floor(r.value * (span + 1));
  if (decisionComplexity >= 4) delayMs += 400;
  if (isHuOrGang) delayMs += 250;
  return { delayMs, rng: r.state };
}

/* ─────────────────────────── §7 决策主循环 ─────────────────────────── */

/** 组装一次决策的最终返回值（含拟人化延迟）。 */
function finalize(
  action: AIAction,
  cfg: DifficultyPreset,
  rng: RngState,
  decisionComplexity: number,
): AIDecision {
  const isHuOrGang =
    action.type === 'HU_ZIMO' ||
    action.type === 'HU_DIANPAO' ||
    action.type === 'HU_QIANGGANG' ||
    action.type === 'GANG';
  const d = computeThinkDelayMs(cfg, rng, decisionComplexity, isHuOrGang);
  return { action, rng: d.rng, thinkDelayMs: d.delayMs };
}

/**
 * AI 决策主循环（§7）。
 *
 * ```
 * switch (obs.phase):
 *   DECIDE_QUE       → pickQue
 *   SELF_ACTION      → HU_ZIMO → 杠 → 出牌（QC-1 强制清缺 + ε 噪声）
 *   RESPONSE_WINDOW  → HU_DIANPAO → 明杠 → 碰 → PASS
 *   QIANG_GANG_WINDOW→ HU_QIANGGANG → PASS
 * ```
 *
 * 反作弊红线：本函数只消费 `AIObservation`（合法可见信息），
 * 不触碰牌墙顺序 / 他家手牌 / RNG 状态以外的任何信息。
 *
 * @param obs       该座位可见信息（宿主从服务端权威模型的投影填充）
 * @param difficulty 难度档位
 * @param rng       当前 PRNG 状态
 * @returns 决策结果。`rng` 为推进后的状态，宿主必须用它替换原状态以保持确定性流。
 */
export function decide(
  obs: AIObservation,
  difficulty: AIDifficulty,
  rng: RngState,
): AIDecision {
  const cfg = DIFFICULTY_PRESETS[difficulty];
  const seen = buildSeenCounts(obs, cfg.seenScope);
  const meldCount = obs.melds.length;

  switch (obs.phase) {
    case 'DECIDE_QUE': {
      const queSuit: Suit = pickQue(obs.hand, difficulty);
      return finalize({ type: 'DECIDE_QUE', queSuit }, cfg, rng, 0);
    }

    case 'SELF_ACTION': {
      // 1) 自摸胡
      if (canWinCounts(obs.hand, meldCount, obs.queSuit) && shouldHu(obs, difficulty)) {
        return finalize({ type: 'HU_ZIMO' }, cfg, rng, 0);
      }
      // 2) 杠（暗杠 / 巴杠）
      for (const g of listSelfGangs(obs)) {
        if (shouldGang(obs, g, difficulty, seen)) {
          return finalize(
            { type: 'GANG', kind: g.kind, gangType: g.gangType, value: gangValue(obs, g, difficulty, seen) },
            cfg,
            rng,
            0,
          );
        }
      }
      // 3) 出牌
      const legal = getLegalDiscards(obs.hand, obs.queSuit); // ★ 含 QC-1 强制打缺过滤
      const ctx = makeValuationContext(obs, seen, cfg);
      const scored = legal.map((t) => ({ tile: t, cost: discardCost(t, ctx) }));
      const pick = pickWithNoise(scored, cfg.epsilon, rng);
      return finalize(
        { type: 'DISCARD', tile: pick.tile, cost: pick.cost },
        cfg,
        pick.rng,
        scored.length,
      );
    }

    case 'RESPONSE_WINDOW': {
      const claimTile = obs.lastDiscard?.tileKey;
      if (claimTile === undefined) {
        // 无牌可响应（异常观察）：安静过
        return finalize({ type: 'PASS' }, cfg, rng, 0);
      }
      // 1) 点炮胡
      const c = copyCounts(obs.hand);
      c[claimTile] = (c[claimTile] ?? 0) + 1;
      if (canWinCounts(c, meldCount, obs.queSuit) && shouldHu(obs, difficulty)) {
        return finalize({ type: 'HU_DIANPAO' }, cfg, rng, 0);
      }
      // 2) 明杠
      if (canMingGang(obs, claimTile)) {
        const g = { kind: kindFromKindIndex(claimTile), gangType: 'MING_GANG' as const };
        if (shouldGang(obs, g, difficulty, seen)) {
          return finalize(
            { type: 'GANG', kind: g.kind, gangType: g.gangType, value: gangValue(obs, g, difficulty, seen) },
            cfg,
            rng,
            0,
          );
        }
      }
      // 3) 碰
      if (canPeng(obs, claimTile)) {
        const p = shouldPeng(obs, claimTile, difficulty, rng);
        if (p.decision) {
          return finalize({ type: 'PENG', tile: claimTile }, cfg, p.rng, 0);
        }
        return finalize({ type: 'PASS' }, cfg, p.rng, 0);
      }
      // 4) 过
      return finalize({ type: 'PASS' }, cfg, rng, 0);
    }

    case 'QIANG_GANG_WINDOW': {
      const claimTile = obs.lastDiscard?.tileKey;
      if (claimTile === undefined) {
        return finalize({ type: 'PASS' }, cfg, rng, 0);
      }
      const c = copyCounts(obs.hand);
      c[claimTile] = (c[claimTile] ?? 0) + 1;
      if (canWinCounts(c, meldCount, obs.queSuit)) {
        return finalize({ type: 'HU_QIANGGANG' }, cfg, rng, 0);
      }
      return finalize({ type: 'PASS' }, cfg, rng, 0);
    }

    default: {
      // 未知阶段（编程错误或未来扩展）：安静过，保持确定性
      return finalize({ type: 'PASS' }, cfg, rng, 0);
    }
  }
}
